import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Rocket, Loader2, Copy, ExternalLink, CheckCircle2, Clock, FileCheck, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';

/**
 * Carte "Onboarding" sur la fiche candidat.
 * Visible quand le candidat est dans un statut "à onboarder" (validated / talent_pool / interviewed).
 */
const ONBOARD_ELIGIBLE_STATUSES = ['validated', 'talent_pool', 'assigned', 'interviewed', 'test_passed'];

const OnboardingCard = ({ candidate }) => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [resending, setResending] = useState(false);

  const candidateId = candidate?._id || candidate?.id;
  const eligible = ONBOARD_ELIGIBLE_STATUSES.includes(candidate?.status);

  const load = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const res = await api.get(`/admin/candidates/${candidateId}/onboarding/session`);
      const data = await res.json();
      setSession(data.session);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  useEffect(() => { load(); }, [load]);

  const start = async () => {
    setStarting(true);
    try {
      const res = await api.post(`/admin/candidates/${candidateId}/onboarding/start`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      toast.success(data.reused ? 'Lien onboarding existant récupéré' : 'Onboarding lancé');
      await load();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setStarting(false);
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/onboarding/${session.token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  const resend = async () => {
    if (session?.status === 'completed') {
      toast.error('Onboarding déjà terminé — pas de renvoi');
      return;
    }
    if (!window.confirm('Renvoyer l\'email d\'onboarding au candidat ? (Force, contourne le garde-fou anti-doublon)')) {
      return;
    }
    setResending(true);
    try {
      const res = await api.post(`/admin/candidates/${candidateId}/onboarding/resend`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast.success('Email d\'onboarding renvoyé au candidat');
      await load();
    } catch (e) {
      toast.error(`Erreur : ${e.message}`);
    } finally {
      setResending(false);
    }
  };

  const portalUrl = session ? `${window.location.origin}/onboarding/${session.token}` : null;

  if (!eligible && !session) {
    // Hide if not eligible and no existing session
    return null;
  }

  return (
    <Card className="p-4 md:p-5 border-tp-primary/20" data-testid="onboarding-card">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-tp-primary/10 flex items-center justify-center flex-shrink-0">
          <Rocket className="h-4 w-4 text-tp-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-display font-semibold text-tp-text">Onboarding</h3>
            {session && <StatusBadge status={session.status} />}
          </div>

          {loading ? (
            <p className="text-xs text-tp-text-light"><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Chargement...</p>
          ) : session ? (
            <div className="space-y-2 mt-2">
              <Progress session={session} />
              <div className="flex flex-wrap gap-2 mt-3">
                <Button onClick={copyLink} size="sm" variant="outline" data-testid="copy-onboarding-link">
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copier le lien
                </Button>
                {session.status !== 'completed' && (
                  <Button
                    onClick={resend}
                    disabled={resending}
                    size="sm"
                    variant="outline"
                    className="text-orange-700 border-orange-300 hover:bg-orange-50"
                    data-testid="resend-onboarding-btn"
                  >
                    {resending ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Renvoyer le mail
                  </Button>
                )}
                <a
                  href={portalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-tp-primary hover:underline px-2 py-1.5"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Aperçu candidat
                </a>
              </div>
              {session.expires_at && session.status !== 'completed' && (
                <p className="text-[10px] text-tp-text-light italic">
                  Expire le {new Date(session.expires_at).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          ) : (
            <>
              <p className="text-xs text-tp-text-light mb-3">
                Envoie un lien magique au candidat pour qu'il complète ses documents,
                signe les conditions et confirme sa disponibilité.
              </p>
              <Button
                onClick={start}
                disabled={starting}
                size="sm"
                className="bg-tp-primary hover:bg-tp-primary-light text-white"
                data-testid="start-onboarding-btn"
              >
                {starting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Rocket className="h-4 w-4 mr-1.5" />}
                Lancer onboarding
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    sent: { label: 'Envoyé', class: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock },
    in_progress: { label: 'En cours', class: 'bg-orange-50 text-orange-700 border-orange-200', icon: Clock },
    completed: { label: 'Terminé', class: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
  };
  const cfg = map[status] || { label: status, class: 'bg-gray-50 text-gray-700 border-gray-200', icon: Clock };
  const Icon = cfg.icon;
  return (
    <Badge className={`${cfg.class} text-[10px]`}>
      <Icon className="h-2.5 w-2.5 mr-1" />
      {cfg.label}
    </Badge>
  );
};

const Progress = ({ session }) => {
  const docs = session.documents || {};
  const docCount = Object.values(docs).reduce((acc, arr) => acc + (Array.isArray(arr) ? arr.length : 0), 0);
  const hasId = (docs.id_document || []).length > 0;
  const hasSig = !!session.signature;
  const hasAvail = !!session.availability_confirmed;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      <Item ok={hasId} label="ID" />
      <Item ok={docCount > 1} label={`Docs (${docCount})`} />
      <Item ok={hasSig} label="Signature" />
      <Item ok={hasAvail} label="Disponibilité" />
    </div>
  );
};

const Item = ({ ok, label }) => (
  <div className={`flex items-center gap-1 text-[11px] ${ok ? 'text-emerald-700' : 'text-tp-text-light'}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <FileCheck className="h-3 w-3" />}
    <span className="truncate">{label}</span>
  </div>
);

export default OnboardingCard;
